import type { OrgDivision, OrgTeam, OrgTeamMember } from '../../../api/admin-org';
import type { UserListItem } from '../../../api/admin-user';
import type { MenuAccessState, MenuCatalogItem, MenuScopeType } from '../../../api/menu-access';
import { judgeMenu, type Judgement, type JudgeViewer, type Verdict } from '../../../lib/menu-judge';

/**
 * 메뉴 권한 화면의 모델 — 조직 트리 · 사람 · 판정 입력 (2026-09-15 · 확정 시안 v4).
 *
 * 조직은 두 단이다(사용자 결정 Q1): **본부**(`organization_divisions`) · **팀**(`organization_teams`).
 * 트리 구분 = 회사 · 본부 · 팀 · 본부 없는 팀 · 팀없는직원.
 *
 * ★ 사람의 판정 팀은 서버가 준 `viewerTeams` 를 쓴다 — 멤버십 목록으로 다시 고르지 않는다
 *   (두 팀 소속자의 「먼저 들어간 활성 팀」을 화면이 따로 계산하면 서버와 갈릴 수 있다).
 */

export type Unit =
  | { kind: 'company' }
  | { kind: 'division'; id: string }
  | { kind: 'team'; id: string }
  | { kind: 'unassigned' };

export interface Person {
  id: string;
  name: string;
  position: string | null;
  /** 소속 팀 — 합류 순. 두 팀 이상이면 화면이 판정 팀을 적는다. */
  teamIds: string[];
  /** 서버가 고른 판정 팀(`viewerTeams`). 없으면 팀 없음 = 부서 기준은 회사 설정. */
  judgeTeamId: string | null;
}

export interface OrgModel {
  divisions: Array<{ id: string; name: string; teamIds: string[] }>;
  teams: Map<string, { id: string; name: string; divisionId: string | null }>;
  /** 본부가 없거나(또는 본부가 비활성인) 팀. */
  looseTeamIds: string[];
  people: Person[];
  byId: Map<string, Person>;
  membersByTeam: Map<string, string[]>;
}

export function buildOrg(input: {
  divisions: OrgDivision[];
  teams: OrgTeam[];
  membersByTeam: Record<string, OrgTeamMember[]>;
  users: UserListItem[];
  viewerTeams: Record<string, string>;
}): OrgModel {
  const activeDivs = input.divisions.filter((d) => d.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const divIds = new Set(activeDivs.map((d) => d.id));
  const activeTeams = input.teams.filter((t) => t.isActive).sort((a, b) => a.sortOrder - b.sortOrder);

  const teams = new Map<string, { id: string; name: string; divisionId: string | null }>();
  for (const t of activeTeams) teams.set(t.id, { id: t.id, name: t.name, divisionId: t.divisionId });

  const roster = input.users.filter((u) => u.isActive !== false);
  const rosterIds = new Set(roster.map((u) => u.id));

  const membersByTeam = new Map<string, string[]>();
  const joined = new Map<string, Array<{ teamId: string; at: string }>>();
  for (const t of activeTeams) {
    const list = (input.membersByTeam[t.id] ?? []).filter((m) => rosterIds.has(m.user.id));
    membersByTeam.set(t.id, list.map((m) => m.user.id));
    for (const m of list) {
      const arr = joined.get(m.user.id) ?? [];
      arr.push({ teamId: t.id, at: m.joinedAt });
      joined.set(m.user.id, arr);
    }
  }

  const people: Person[] = roster.map((u) => ({
    id: u.id,
    name: u.name,
    position: u.position,
    teamIds: (joined.get(u.id) ?? []).slice().sort((a, b) => a.at.localeCompare(b.at)).map((j) => j.teamId),
    judgeTeamId: input.viewerTeams[u.id] ?? null,
  }));

  return {
    divisions: activeDivs.map((d) => ({
      id: d.id,
      name: d.name,
      teamIds: activeTeams.filter((t) => t.divisionId === d.id).map((t) => t.id),
    })),
    teams,
    looseTeamIds: activeTeams.filter((t) => !t.divisionId || !divIds.has(t.divisionId)).map((t) => t.id),
    people,
    byId: new Map(people.map((p) => [p.id, p])),
    membersByTeam,
  };
}

export function peopleOf(org: OrgModel, unit: Unit): Person[] {
  switch (unit.kind) {
    case 'company':
      return org.people;
    case 'team':
      return (org.membersByTeam.get(unit.id) ?? [])
        .map((id) => org.byId.get(id))
        .filter((p): p is Person => !!p);
    case 'division': {
      const inDiv = new Set(org.divisions.find((d) => d.id === unit.id)?.teamIds ?? []);
      return org.people.filter((p) => p.teamIds.some((t) => inDiv.has(t)));
    }
    case 'unassigned':
      return org.people.filter((p) => !p.judgeTeamId);
  }
}

/** 부서별 탭의 판정 입력 — 사람 없이 그 조직 단위만. */
export function unitViewer(org: OrgModel | null, unit: Unit): JudgeViewer {
  if (unit.kind === 'team') {
    return { userId: null, teamId: unit.id, divisionId: org?.teams.get(unit.id)?.divisionId ?? null };
  }
  if (unit.kind === 'division') return { userId: null, teamId: null, divisionId: unit.id };
  return { userId: null, teamId: null, divisionId: null };
}

export function personViewer(org: OrgModel, p: Person): JudgeViewer {
  const team = p.judgeTeamId ? org.teams.get(p.judgeTeamId) : undefined;
  return { userId: p.id, teamId: p.judgeTeamId, divisionId: team?.divisionId ?? null };
}

export function judgeFor(state: MenuAccessState, menu: MenuCatalogItem, viewer: JudgeViewer): Judgement {
  const setting = state.company.find((c) => c.menuKey === menu.menuKey);
  return judgeMenu({
    catalogEnabled: true,
    companyEnabled: setting ? setting.enabled : null,
    moduleEnabled: menu.moduleEnabled !== false,
    rules: state.rules.filter((r) => r.menuKey === menu.menuKey),
    viewer,
  });
}

/** 한 대상 · 한 메뉴에 걸린 값 — 같은 줄이 여럿이면 하나라도 허용이면 허용(서버와 같다). */
export function ruleAt(state: MenuAccessState, scopeType: MenuScopeType, scopeId: string, menuKey: string): Verdict {
  const hit = state.rules.filter((r) => r.scopeType === scopeType && r.scopeId === scopeId && r.menuKey === menuKey);
  if (hit.length === 0) return 'none';
  return hit.some((r) => r.allow) ? 'allow' : 'deny';
}

export function rulesOf(state: MenuAccessState, scopeType: MenuScopeType, scopeId: string) {
  return state.rules.filter((r) => r.scopeType === scopeType && r.scopeId === scopeId);
}

/** 칸 저장 결과를 화면 상태에 반영 — 그 칸의 줄을 지우고, 상속이 아니면 한 줄을 넣는다. */
export function applyCell(
  state: MenuAccessState, scopeType: MenuScopeType, scopeId: string, menuKey: string, next: Verdict,
): MenuAccessState {
  const rest = state.rules.filter((r) => !(r.scopeType === scopeType && r.scopeId === scopeId && r.menuKey === menuKey));
  return {
    ...state,
    rules: next === 'none' ? rest : [...rest, { menuKey, scopeType, scopeId, allow: next === 'allow' }],
  };
}

export const SECTION_ORDER = ['primary', 'secondary', 'status', 'aux', 'mobile'];
export const SECTION_LABEL: Record<string, { title: string; note?: string }> = {
  primary: { title: '레일 상단' },
  secondary: { title: '레일 하단' },
  status: { title: '하단 상태바' },
  aux: { title: '보조영역', note: '전체메뉴에만' },
  mobile: { title: '앱 전용', note: '웹에는 안 뜨고 모바일 앱에만 보입니다' },
};

/** 놓인 차례 — 회사가 정한 값이 있으면 그것, 없으면 카탈로그 차례. */
export function orderedMenus(state: MenuAccessState): MenuCatalogItem[] {
  const bySort = new Map(state.company.map((c) => [c.menuKey, c.sortOrder]));
  return [...state.menus].sort(
    (a, b) => (bySort.get(a.menuKey) ?? a.sortOrder) - (bySort.get(b.menuKey) ?? b.sortOrder),
  );
}

export function groupBySection(menus: MenuCatalogItem[]): Array<[string, MenuCatalogItem[]]> {
  const map = new Map<string, MenuCatalogItem[]>();
  for (const m of menus) {
    const list = map.get(m.section);
    if (list) list.push(m); else map.set(m.section, [m]);
  }
  const rank = (s: string) => { const i = SECTION_ORDER.indexOf(s); return i < 0 ? 99 : i; };
  return [...map.entries()].sort((a, b) => rank(a[0]) - rank(b[0]));
}

export function unitLabel(org: OrgModel | null, unit: Unit): string {
  if (unit.kind === 'company') return '회사 전체';
  if (unit.kind === 'unassigned') return '팀없는직원';
  if (unit.kind === 'team') return org?.teams.get(unit.id)?.name ?? '팀';
  return org?.divisions.find((d) => d.id === unit.id)?.name ?? '본부';
}

export function sameUnit(a: Unit, b: Unit): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'team' || a.kind === 'division') return a.id === (b as { id: string }).id;
  return true;
}

/** 결과 칸의 출처 글자 — 「← 경영지원본부 허용」 · 「회사에서 끔」 · 「회사 기본」. */
export function sourceText(org: OrgModel | null, j: Judgement): string {
  if (j.blockedBy === 'company') return '회사에서 끔';
  if (j.blockedBy === 'module') return '계약 모듈 꺼짐';
  if (j.base.source === 'company') return '회사 기본';
  const name = j.base.source === 'team'
    ? org?.teams.get(j.base.sourceId ?? '')?.name ?? '팀'
    : org?.divisions.find((d) => d.id === j.base.sourceId)?.name ?? '본부';
  return `← ${name} ${j.base.verdict === 'allow' ? '허용' : '차단'}`;
}
